import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { Anchor, BadgeCheck, CalendarDays, Camera, ChevronLeft, ChevronRight, Compass, Crown, Gift, Mail, Phone, ScrollText, Settings, ShieldCheck, Sparkles, Trophy, UserRound, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '../../lib/analytics';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function mediaUrl(url) {
  if (!url) return '';
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  return `${API_BASE}${url.startsWith('/') ? url : `/${url}`}`;
}

function money(value) {
  return `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function tierColor(tier, fallback = '#D4AF37') {
  return tier?.color || tier?.tier_color || fallback;
}

function tierName(tier) {
  return tier?.name || tier?.tier_name || 'Bronze';
}

function tierEmoji(tier) {
  return tier?.emoji || tier?.icon || '🏅';
}

function tierRequirement(tier) {
  return Number(tier?.min_total_deposit || tier?.min_deposit || tier?.required_deposit || 0);
}

function tierBenefits(tier) {
  const configured = tier?.perks || tier?.benefits || [];
  const bonus = Number(tier?.deposit_bonus_pct || tier?.deposit_bonus || 0);
  return [
    bonus > 0 ? `${bonus}% deposit bonus in FP` : 'Deposit bonuses controlled by admin rules',
    ...configured,
    'Priority support review',
    'Tier progress tracking',
  ].filter(Boolean).slice(0, 4);
}

export default function ProfileTab({ user, myTier, goBack, onSettings, checkAuth }) {
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [tiersOpen, setTiersOpen] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState(null);

  const displayName = user?.username || user?.full_name || 'Player';
  const handle = user?.username || user?.referral_code || user?.email || 'vegasplayer';
  const activeTierName = myTier?.tier_name || user?.achievement_tier || 'Bronze';
  const currentTier = useMemo(() => {
    const match = tiers.find((t) => tierName(t).toLowerCase() === String(activeTierName).toLowerCase());
    return match || myTier?.current_tier || tiers[0] || { name: activeTierName, emoji: '🏅', color: '#D4AF37' };
  }, [tiers, activeTierName, myTier]);
  const selectedTier = tiers.find((t) => (t.id || t.name) === selectedTierId) || currentTier;
  const currentColor = tierColor(currentTier);
  const phoneVerified = user?.phone_verified || user?.phone_verified_at;
  const avatarSrc = mediaUrl(avatarUrl || user?.avatar_url);

  useEffect(() => {
    api.get('/api/tiers').then((r) => {
      const list = r.data?.tiers || [];
      setTiers(list);
      const match = list.find((t) => tierName(t).toLowerCase() === String(activeTierName).toLowerCase()) || list[0];
      if (match) setSelectedTierId(match.id || match.name);
    }).catch(() => {});
  }, [activeTierName]);

  const handleAvatarUpload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Choose an image file');
      return;
    }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('upload_type', 'avatar');
      const upload = await api.post('/api/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const nextUrl = upload.data?.public_url || upload.data?.url;
      if (!nextUrl) throw new Error('Upload did not return a URL');
      await api.patch('/api/auth/profile', { avatar_url: nextUrl });
      setAvatarUrl(nextUrl);
      trackEvent('profile_avatar_updated');
      toast.success('Profile picture updated');
      await checkAuth?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to upload profile picture');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const progressTarget = Number(myTier?.next_tier?.min_total_deposit || myTier?.next_tier?.min_deposit || 10000);
  const progressValue = Number(myTier?.total_deposit_sc || myTier?.total_deposit || 0);
  const progressPct = progressTarget > 0 ? Math.min(100, Math.round((progressValue / progressTarget) * 100)) : 100;
  const nextTierName = myTier?.next_tier?.tier_name || myTier?.next_tier?.name || 'Next Rank';

  return (
    <div
      className="vl-captain-profile vl-captain-profile--log min-h-full px-4 py-3 pb-28 text-white vl-readable-player"
      data-testid="profile-tab"
    >
      <div className="vl-captain-profile__sea" aria-hidden="true"><i /><b /></div>
      <div className="vl-captain-profile__sheet max-w-3xl mx-auto">
        <header className="vl-profile-logbar">
          <button
            type="button"
            onClick={goBack}
            className="vl-profile-round-action"
            data-testid="profile-back"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="vl-profile-logbar__title">
            <span><Anchor /> CAPTAIN'S LOG</span>
            <strong>Identity &amp; Rank</strong>
          </div>
          <button
            type="button"
            onClick={onSettings}
            className="vl-profile-round-action"
            data-testid="profile-settings-link"
            aria-label="Settings"
          >
            <Settings className="w-5 h-5" />
          </button>
        </header>

        <section className="vl-captain-profile__hero vl-profile-passport">
          <div className="vl-profile-passport__stamp" aria-hidden="true"><Compass /></div>
          <div className="vl-profile-passport__identity">
            <label className="vl-profile-avatar cursor-pointer group" data-testid="profile-photo-upload">
              <span className="vl-profile-avatar__ring" style={{ '--rank-color': currentColor }}>
                {avatarSrc ? (
                  <img src={avatarSrc} alt={displayName} />
                ) : (
                  <span>{displayName.charAt(0).toUpperCase()}</span>
                )}
              </span>
              <span className="vl-profile-avatar__camera">
                {uploadingAvatar ? <span className="spinner w-4 h-4" /> : <Camera className="w-4 h-4" />}
              </span>
              <input type="file" accept="image/*" className="hidden" disabled={uploadingAvatar} onChange={(e) => handleAvatarUpload(e.target.files?.[0])} />
            </label>
            <div className="vl-profile-passport__name">
              <small>REGISTERED CAPTAIN</small>
              <h2>{displayName}</h2>
              <p>@{String(handle).replace(/^#/, '')}</p>
              <div className="vl-profile-passport__badges">
                <span><BadgeCheck /> Active Crew</span>
                <span style={{ '--rank-color': currentColor }}><Crown /> {tierName(currentTier)}</span>
              </div>
            </div>
          </div>
          <div className="vl-profile-passport__footer">
            <span><Compass /> PORT <b>VEGAS LOBBY</b></span>
            <span><ShieldCheck /> STATUS <b>READY TO PLAY</b></span>
          </div>
        </section>

        <section
          className="vl-profile-rank-card"
          data-testid="profile-tier-card"
        >
          <div className="vl-profile-rank-card__top">
            <div className="vl-profile-rank-card__medal" style={{ '--rank-color': currentColor }}>
              {tierEmoji(currentTier)}
            </div>
            <div className="vl-profile-rank-card__copy">
              <small>CURRENT SHIP RANK</small>
              <h3>{tierName(currentTier)}</h3>
              <p>{money(progressValue)} secured toward {nextTierName}</p>
            </div>
            <strong>{progressPct}%</strong>
          </div>
          <div className="vl-profile-rank-card__route" style={{ '--progress': `${progressPct}%` }}>
            <span style={{ width: `${progressPct}%`, '--rank-color': currentColor }} />
            <i aria-hidden="true" />
          </div>
          <div className="vl-profile-rank-card__ports">
            <span>{tierName(currentTier)}</span>
            <span>{money(progressTarget)}</span>
          </div>

          <button
            type="button"
            onClick={() => setTiersOpen((v) => !v)}
            className="vl-profile-rank-card__map"
            data-testid="profile-view-all-tiers"
          >
            <span><ScrollText /> Open Rank Map</span>
            <ChevronRight className={`w-5 h-5 transition ${tiersOpen ? 'rotate-90' : ''}`} />
          </button>

          {tiersOpen && (
            <div className="mt-4 space-y-3" data-testid="profile-tier-list">
              {(tiers.length ? tiers : [currentTier]).map((tier) => {
                const id = tier.id || tier.name;
                const active = id === (selectedTier.id || selectedTier.name);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setSelectedTierId(id)}
                    className="w-full rounded-2xl p-3 text-left transition flex items-start gap-3"
                    style={{
                      background: active ? `${tierColor(tier)}24` : 'rgba(255,255,255,0.045)',
                      border: `1px solid ${active ? tierColor(tier) : 'rgba(255,255,255,0.10)'}`,
                    }}
                    data-testid={`profile-tier-${String(tierName(tier)).toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shrink-0" style={{ background: `${tierColor(tier)}18` }}>
                      {tierEmoji(tier)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-base font-black">{tierName(tier)}</span>
                        <span className="text-xs font-bold text-gray-300">{money(tierRequirement(tier))} required</span>
                      </span>
                      <span className="mt-1 flex flex-wrap gap-1.5">
                        {tierBenefits(tier).slice(0, 3).map((benefit) => (
                          <span key={benefit} className="rounded-full px-2 py-1 text-[11px] font-bold bg-white/5 text-gray-200 border border-white/10">
                            {benefit}
                          </span>
                        ))}
                      </span>
                    </span>
                    <ChevronRight className="w-5 h-5 mt-4 shrink-0 text-gray-400" />
                  </button>
                );
              })}
            </div>
          )}

          <div className="vl-profile-perks" data-testid="profile-tier-detail">
            <div className="vl-profile-section-title">
              <span><Sparkles /> RANK TREASURE</span>
              <strong>{tierName(selectedTier)} Benefits</strong>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {[
                { icon: Gift, label: `${Number(selectedTier.deposit_bonus_pct || selectedTier.deposit_bonus || 0)}% FP bonus`, sub: 'Deposit bonus only' },
                { icon: Crown, label: 'Higher Rewards', sub: 'More perks' },
                { icon: Zap, label: 'Faster Withdrawals', sub: 'Priority review' },
                { icon: Trophy, label: 'Exclusive Events', sub: 'Tier access' },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="rounded-xl p-2.5 text-center" style={{ background: 'rgba(0,0,0,0.16)' }}>
                    <Icon className="w-5 h-5 mx-auto mb-1.5" style={{ color: currentColor }} />
                    <p className="text-xs font-black">{item.label}</p>
                    <p className="text-[11px] text-gray-300">{item.sub}</p>
                  </div>
                );
              })}
            </div>
            {(selectedTier.perks || selectedTier.benefits || []).length > 0 && (
              <div className="mt-2 space-y-1">
                {(selectedTier.perks || selectedTier.benefits || []).slice(0, 6).map((perk, idx) => (
                  <p key={idx} className="text-xs text-gray-200 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-[#F5D76E]" /> {perk}</p>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="vl-profile-manifest" data-testid="profile-info-summary">
          <div className="vl-profile-manifest__heading">
            <div className="vl-profile-section-title">
              <span><ScrollText /> SHIP MANIFEST</span>
              <strong>Captain Details</strong>
            </div>
            <button type="button" onClick={onSettings}>Edit <ChevronRight /></button>
          </div>
          <div className="rounded-2xl p-3 bg-white/[0.05] border border-[#D4AF37]/15">
            <p className="text-xs text-gray-300 mb-1 flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> Email</p>
            <p className="text-sm font-black truncate">{user?.email || 'Not set'}</p>
          </div>
          <div className="rounded-2xl p-3 bg-white/[0.05] border border-[#D4AF37]/15">
            <p className="text-xs text-gray-300 mb-1 flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> Phone</p>
            <p className="text-sm font-black truncate">{user?.phone_number || 'Not set'}</p>
            <p className={`text-xs mt-1 ${phoneVerified ? 'text-emerald-300' : 'text-amber-300'}`}>{phoneVerified ? 'Verified' : 'Pending manual verification'}</p>
          </div>
          <div className="rounded-2xl p-3 bg-white/[0.05] border border-[#D4AF37]/15">
            <p className="text-xs text-gray-300 mb-1 flex items-center gap-2"><UserRound className="w-3.5 h-3.5" /> Full Name</p>
            <p className="text-base font-black">{user?.full_name || 'Not set'}</p>
          </div>
          <div className="rounded-2xl p-3 bg-white/[0.05] border border-[#D4AF37]/15">
            <p className="text-xs text-gray-300 mb-1 flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> Date of Birth</p>
            <p className="text-base font-black">{user?.date_of_birth || user?.dob || 'Not set'}</p>
          </div>
          <div className="rounded-2xl p-3 bg-white/[0.05] border border-[#D4AF37]/15 sm:col-span-2">
            <p className="text-xs text-gray-300 mb-1 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Referral Code</p>
            <p className="text-base font-black font-mono">{user?.referral_code || 'N/A'}</p>
          </div>
        </section>
      </div>
    </div>
  );
}
